import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CheckCircle2, Mail, Smartphone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A newly registered company must confirm its e-mail address AND mobile number (one-time codes) before the admin panel opens.
 * Renders its children as soon as the server says the account is verified (or never needed verification).
 */
export default function VerificationGate({ token, onLogout, children }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/verification/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(r.data);
    } catch (e) {
      // old servers without the endpoint / network issues must not lock existing customers out
      if (e.response?.status === 401) setError(true);
      else setStatus({ required: false });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (error) return children;
  if (!status) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
  if (!status.required) return children;

  return (
    <div className="min-h-screen bg-zinc-50 flex items-start sm:items-center justify-center px-4 py-8" data-testid="verification-gate">
      <div className="w-full max-w-md space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Verifiera ditt konto</h1>
          <p className="text-sm text-zinc-600 mt-1">
            För att skydda kontot behöver vi bekräfta att du äger både e-postadressen och mobilnumret. Därefter kan du börja använda Servora.
          </p>
        </div>
        <Step icon={Mail} title="E-post" target={status.email} done={status.email_verified} channel="email" headers={headers} onDone={setStatus} />
        <Step icon={Smartphone} title="Mobilnummer" target={status.phone} done={status.phone_verified} channel="phone" headers={headers} onDone={setStatus} canEdit />
        <button onClick={onLogout} className="text-xs text-zinc-500 underline" data-testid="verification-logout">Logga ut</button>
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, target, done, channel, headers, onDone, canEdit }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [editing, setEditing] = useState(false);
  const [newPhone, setNewPhone] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/verification/${channel}/send`, {}, { headers });
      setSent(true); setCooldown(60); setCode("");
      toast.success(channel === "email" ? "Koden är skickad till din e-post" : "Koden är skickad via SMS");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skicka koden");
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/verification/${channel}/confirm`, { code }, { headers });
      onDone(r.data);
      if (r.data.complete) window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel kod");
    }
    setBusy(false);
  };

  const savePhone = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/verification/contact`, { phone: newPhone }, { headers });
      onDone(r.data); setEditing(false); setSent(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ogiltigt nummer");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid={`verify-${channel}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-zinc-500" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900">{title}</p>
          <p className="text-xs text-zinc-500 truncate">{target}</p>
        </div>
        {done && <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Verifierad</span>}
      </div>
      {!done && (
        <>
          {!sent ? (
            <Button onClick={send} disabled={busy} className="w-full" data-testid={`verify-${channel}-send`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Skicka kod"}
            </Button>
          ) : (
            <div className="space-y-2">
              <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric"
                autoComplete="one-time-code" placeholder="6-siffrig kod" maxLength={6} className="text-center text-lg tracking-widest"
                data-testid={`verify-${channel}-code`} />
              <Button onClick={confirm} disabled={busy || code.length !== 6} className="w-full" data-testid={`verify-${channel}-confirm`}>Bekräfta</Button>
              <button onClick={send} disabled={busy || cooldown > 0} className="text-xs text-zinc-500 underline disabled:no-underline disabled:opacity-60">
                {cooldown > 0 ? `Skicka ny kod om ${cooldown} s` : "Skicka ny kod"}
              </button>
            </div>
          )}
          {canEdit && (
            editing ? (
              <div className="flex gap-2">
                <Input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="070-123 45 67" inputMode="tel" />
                <Button variant="outline" onClick={savePhone} disabled={busy}>Spara</Button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="text-xs text-zinc-500 underline">Fel nummer? Ändra</button>
            )
          )}
        </>
      )}
    </div>
  );
}
